import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, Check, X, Music, FileAudio, Mic, File } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const SessionUploadPortal = () => {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [uploadsRemaining, setUploadsRemaining] = useState(5);
  const [maxFileSize, setMaxFileSize] = useState(500);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('beat');
  const [uploadHistory, setUploadHistory] = useState([]);

  useEffect(() => {
    fetchBookingInfo();
  }, [token]);

  const fetchBookingInfo = async () => {
    try {
      const response = await axios.get(`${API}/session-upload/${token}`);
      setBooking(response.data.booking);
      setUploadsRemaining(response.data.uploads_remaining);
      setMaxFileSize(response.data.max_file_size_mb);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching booking info:', error);
      toast.error('Invalid or expired upload link');
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxFileSize) {
      toast.error(`File size must be under ${maxFileSize}MB`);
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    if (!fileType) {
      toast.error('Please select a file type');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('file_type', fileType);

      const response = await axios.post(
        `${API}/session-upload/${token}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      toast.success(`File uploaded successfully! ${response.data.remaining} uploads remaining`);
      
      // Add to upload history
      setUploadHistory([...uploadHistory, {
        filename: selectedFile.name,
        type: fileType,
        size: (selectedFile.size / (1024 * 1024)).toFixed(2)
      }]);

      // Reset
      setSelectedFile(null);
      setFileType('beat');
      document.getElementById('fileInput').value = '';

      // Refresh booking info
      fetchBookingInfo();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'beat': return <Music className="text-purple-400" size={20} />;
      case 'reference': return <FileAudio className="text-blue-400" size={20} />;
      case 'vocal': return <Mic className="text-pink-400" size={20} />;
      default: return <File className="text-gray-400" size={20} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gradient-to-br from-red-900/20 to-gray-900/20 backdrop-blur-sm border border-red-500/30 rounded-xl p-8 text-center">
          <X className="text-red-400 mx-auto mb-4" size={48} />
          <h2 className="text-2xl font-bold text-white mb-2">Invalid Link</h2>
          <p className="text-gray-400">This upload link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            Session File Upload
          </h1>
          <p className="text-gray-400">Upload beats, references, or any files for your session</p>
        </div>

        {/* Booking Info */}
        <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 mb-8">
          <h3 className="text-xl font-bold text-white mb-4">Session Details</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-400">Customer</p>
              <p className="text-white font-semibold">{booking.customer_name}</p>
            </div>
            <div>
              <p className="text-gray-400">Engineer</p>
              <p className="text-white font-semibold">{booking.engineer_name}</p>
            </div>
            <div>
              <p className="text-gray-400">Date & Time</p>
              <p className="text-white font-semibold">{booking.date} at {booking.time}</p>
            </div>
            <div>
              <p className="text-gray-400">Service</p>
              <p className="text-white font-semibold">{booking.service}</p>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        {uploadsRemaining > 0 ? (
          <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-white mb-4">Upload Files</h3>
            
            <div className="space-y-6">
              {/* File Type Selector */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-3">File Type</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'beat', label: 'Beat', icon: Music },
                    { value: 'reference', label: 'Reference', icon: FileAudio },
                    { value: 'vocal', label: 'Vocal', icon: Mic },
                    { value: 'other', label: 'Other', icon: File }
                  ].map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => setFileType(type.value)}
                        className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center space-y-2 ${
                          fileType === type.value
                            ? 'border-purple-500 bg-purple-500/20'
                            : 'border-purple-500/20 hover:border-purple-500/50'
                        }`}
                      >
                        <Icon size={24} />
                        <span className="text-sm font-semibold">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* File Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-3">Select File</label>
                <input
                  id="fileInput"
                  type="file"
                  onChange={handleFileSelect}
                  accept=".mp3,.wav,.m4a,.aiff,.flac,.zip,.pdf,.txt"
                  className="block w-full text-sm text-gray-400
                    file:mr-4 file:py-3 file:px-6
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-purple-600 file:text-white
                    hover:file:bg-purple-700 file:cursor-pointer
                    cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Accepted: MP3, WAV, M4A, AIFF, FLAC, ZIP, PDF, TXT (Max {maxFileSize}MB)
                </p>
              </div>

              {/* Selected File Preview */}
              {selectedFile && (
                <div className="bg-black/40 rounded-lg p-4 border border-purple-500/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getFileTypeIcon(fileType)}
                      <div>
                        <p className="text-white font-semibold">{selectedFile.name}</p>
                        <p className="text-sm text-gray-400">
                          {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                    >
                      <X size={20} className="text-red-400" />
                    </button>
                  </div>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload size={20} />
                    <span>Upload File</span>
                  </>
                )}
              </button>

              <p className="text-center text-sm text-gray-400">
                {uploadsRemaining} upload{uploadsRemaining !== 1 ? 's' : ''} remaining
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 backdrop-blur-sm border border-green-500/30 rounded-xl p-8 text-center mb-8">
            <Check className="text-green-400 mx-auto mb-4" size={48} />
            <h3 className="text-xl font-bold text-white mb-2">All Files Uploaded!</h3>
            <p className="text-gray-400">You've reached the maximum of 5 files for this session.</p>
          </div>
        )}

        {/* Upload History */}
        {uploadHistory.length > 0 && (
          <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
            <h3 className="text-xl font-bold text-white mb-4">Uploaded Files</h3>
            <div className="space-y-3">
              {uploadHistory.map((file, index) => (
                <div key={index} className="bg-black/40 rounded-lg p-4 flex items-center space-x-3">
                  <Check className="text-green-400" size={20} />
                  <div className="flex-1">
                    <p className="text-white font-semibold">{file.filename}</p>
                    <p className="text-sm text-gray-400">
                      {file.type.charAt(0).toUpperCase() + file.type.slice(1)} • {file.size} MB
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionUploadPortal;
